import { ChangeEvent, FocusEvent, forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import { usePermissionsContext } from "../../../context/permissionContext";
import { createLoadOptions } from "../../../hooks/useDebounce";
import { useI18n } from "../../../hooks/useTranslations";
import useCostCenterApi from "../../../http/costCenter/api";
import { CostCenter } from "../../../http/costCenter/types";
import useOfferAcceptorsApi from "../../../http/offerAcceptor/api";
import { OfferAcceptor } from "../../../http/offerAcceptor/type";
import usePriorityApi from "../../../http/priority/api";
import { Priority } from "../../../http/priority/types";
import useProcessingTypeApi from "../../../http/processingType/api";
import { ProcessingType } from "../../../http/processingType/type";
import useSupplierApi from "../../../http/supplier/api";
import { Supplier } from "../../../http/supplier/types";
import { PaginationQuery } from "../../../types";
import CheckBox from "../../ui/CheckBox";
import Form from "../../ui/Form";
import FormField from "../../ui/FormField";
import Inline from "../../ui/Inline";
import Input from "../../ui/Input";
import reactSelectStyles from "../../ui/ReactSelectUtils";

export type GeneralDataFormHandle = {
  validate: () => Promise<boolean>;
};

export type GeneralDataFormBody = {
  type: ProcessingType | null;
  priority: Priority;
  notes: string | null;
  supplier: Supplier | null;
  costCenter: CostCenter | null;
  pickingNumber: string | null;
  offerMandatory?: boolean;
  offerAcceptor: OfferAcceptor | null;
  isKit: boolean;
};

type GeneralDataFormProps = {
  onChange: (body: GeneralDataFormBody, valid: boolean) => Promise<void>;
  isDisabled?: boolean;
  isLoading?: boolean;
};

const GeneralDataForm = forwardRef<GeneralDataFormHandle, GeneralDataFormProps>(
  ({ onChange, isDisabled, isLoading }, ref) => {
    const i18n = useI18n();
    const permissions = usePermissionsContext();

    const { useProcessingTypes } = useProcessingTypeApi();
    const { searchPriorities } = usePriorityApi();
    const { searchSuppliers } = useSupplierApi();
    const { searchCostCenters } = useCostCenterApi();
    const { searchOfferAcceptors } = useOfferAcceptorsApi();

    const defaultProcessingTypesOptions = useProcessingTypes({ limit: 100 });

    const [defaultPrioritiesOptions, setDefaultPrioritiesOptions] = useState<Priority[]>([]);
    const [defaultSuppliersOptions, setDefaultSuppliersOptions] = useState<Supplier[]>([]);
    const [defaultCostCentersOptions, setDefaultCostCentersOptions] = useState<CostCenter[]>([]);
    const [defaultOfferAcceptorOptions, setDefaultOfferAcceptorOptions] = useState<OfferAcceptor[]>([]);

    return (
      <Form<GeneralDataFormBody> disabled={isDisabled} mode="onChange">
        {({ register, control, watch, trigger, setValue }) => {
          useImperativeHandle(ref, () => ({
            async validate() {
              return await trigger();
            },
          }));

          const selectedType = watch("type");
          const offerMandatory = watch("offerMandatory");

          const submit = async () => {
            await trigger();
            const body = watch();
            onChange(body, !!body.type?.id);
          };

          return (
            <>
              <Controller
                name="type"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.t.common.requestType} error={fieldState.error}>
                    {({ labelId, isInvalid }) => (
                      <Select<ProcessingType>
                        {...field}
                        inputId={labelId}
                        options={defaultProcessingTypesOptions.data?.items}
                        isDisabled={isDisabled || isLoading}
                        placeholder={i18n.t.common.select + "..."}
                        getOptionLabel={(o) => o.name}
                        getOptionValue={(o) => o.id}
                        styles={reactSelectStyles(isInvalid)}
                        onChange={(value) => {
                          field.onChange(value);
                          submit();
                        }}
                      />
                    )}
                  </FormField>
                )}
              />

              <Inline columnGap={1}>
                <FormField label={i18n.t.common.producesKit + ":"} children={undefined}></FormField>
                <CheckBox
                  {...register("isKit")}
                  disabled={isDisabled || isLoading}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    setValue("isKit", e.target.checked);
                    submit();
                  }}
                />
              </Inline>

              <Controller
                name="priority"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.t.common.priority} error={fieldState.error}>
                    {({ labelId, isInvalid }) => {
                      const searchTerm = useRef("");
                      const aborter = useRef<AbortController | undefined>(undefined);
                      const loadOptions = createLoadOptions<Priority>(aborter, searchTerm, searchPriorities);

                      useEffect(() => {
                        loadOptions(searchTerm.current).then(setDefaultPrioritiesOptions);
                      }, []);

                      return (
                        <AsyncSelect<Priority>
                          {...field}
                          inputId={labelId}
                          defaultOptions={defaultPrioritiesOptions}
                          loadOptions={(st) => loadOptions(st)}
                          getOptionLabel={(o) => o.name}
                          getOptionValue={(o) => o.id}
                          placeholder={i18n.t.common.select + "..."}
                          isDisabled={isDisabled || isLoading}
                          styles={reactSelectStyles(isInvalid)}
                          onChange={(value) => {
                            field.onChange(value);
                            submit();
                          }}
                        />
                      );
                    }}
                  </FormField>
                )}
              />

              <FormField label={i18n.t.notes.plural} isOptional>
                {({ labelId }) => (
                  <Input
                    id={labelId}
                    {...register("notes")}
                    disabled={isDisabled || isLoading}
                    onBlur={(e: FocusEvent<HTMLInputElement>) => {
                      setValue("notes", e.target.value);
                      submit();
                    }}
                  />
                )}
              </FormField>

              {selectedType?.showMarketingFields && (
                <Controller
                  name="supplier"
                  control={control}
                  render={({ field, fieldState }) => (
                    <FormField label={i18n.t.suppliers.singular} error={fieldState.error}>
                      {({ labelId, isInvalid }) => {
                        const searchTerm = useRef("");
                        const aborter = useRef<AbortController | undefined>(undefined);
                        const loadOptions = createLoadOptions<Supplier>(aborter, searchTerm, searchSuppliers);

                        useEffect(() => {
                          loadOptions(searchTerm.current).then(setDefaultSuppliersOptions);
                        }, []);

                        return (
                          <AsyncSelect<Supplier>
                            {...field}
                            inputId={labelId}
                            defaultOptions={defaultSuppliersOptions}
                            loadOptions={(st) => loadOptions(st)}
                            getOptionLabel={(o) => o.name}
                            getOptionValue={(o) => o.code}
                            isClearable
                            placeholder={i18n.t.common.select + "..."}
                            isDisabled={isDisabled || isLoading}
                            styles={reactSelectStyles(isInvalid)}
                            onChange={(value) => {
                              field.onChange(value);
                              submit();
                            }}
                          />
                        );
                      }}
                    </FormField>
                  )}
                />
              )}

              <Controller
                name="costCenter"
                control={control}
                render={({ field }) => (
                  <FormField label={i18n.t.costCenters.singular} isOptional>
                    {({ labelId, isInvalid }) => {
                      const searchTerm = useRef("");
                      const aborter = useRef<AbortController | undefined>(undefined);
                      const loadOptions = createLoadOptions<CostCenter>(aborter, searchTerm, searchCostCenters);

                      useEffect(() => {
                        loadOptions(searchTerm.current).then(setDefaultCostCentersOptions);
                      }, []);

                      return (
                        <AsyncSelect<CostCenter>
                          {...field}
                          inputId={labelId}
                          defaultOptions={defaultCostCentersOptions}
                          loadOptions={(st) => loadOptions(st)}
                          getOptionLabel={(o) => `${o.code} - ${o.name}`}
                          getOptionValue={(o) => o.code}
                          isClearable
                          placeholder={i18n.t.common.select + "..."}
                          isDisabled={isDisabled || isLoading}
                          styles={reactSelectStyles(isInvalid)}
                          onChange={(value) => {
                            field.onChange(value);
                            submit();
                          }}
                        />
                      );
                    }}
                  </FormField>
                )}
              />

              <FormField label={i18n.t.common.productionPickingNumber} isOptional>
                {({ labelId }) => (
                  <Input
                    id={labelId}
                    {...register("pickingNumber")}
                    disabled={isDisabled || isLoading}
                    onBlur={(e: FocusEvent<HTMLInputElement>) => {
                      setValue("pickingNumber", e.target.value);
                      submit();
                    }}
                  />
                )}
              </FormField>

              <Inline columnGap={1}>
                <FormField label={i18n.t.common.offerMandatory + ":"} children={undefined}></FormField>
                <CheckBox
                  {...register("offerMandatory")}
                  disabled={isDisabled || isLoading}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    setValue("offerMandatory", e.target.checked);
                    submit();
                  }}
                />
              </Inline>

              {offerMandatory && permissions.isWarehouseOperator && (
                <Controller
                  name="offerAcceptor"
                  control={control}
                  rules={{ required: offerMandatory && permissions.isWarehouseOperator }}
                  render={({ field, fieldState }) => (
                    <FormField label={i18n.t.common.sendOfferTo} error={fieldState.error}>
                      {({ labelId, isInvalid }) => {
                        const searchTerm = useRef("");
                        const aborter = useRef<AbortController | undefined>(undefined);
                        const loadOptions = createLoadOptions<OfferAcceptor>(
                          aborter,
                          searchTerm,
                          async (query: PaginationQuery, signal?: AbortSignal) => {
                            const data = await searchOfferAcceptors(query, signal);
                            return data;
                          },
                        );

                        useEffect(() => {
                          loadOptions(searchTerm.current).then(setDefaultOfferAcceptorOptions);
                        }, []);

                        return (
                          <AsyncSelect<OfferAcceptor>
                            {...field}
                            inputId={labelId}
                            defaultOptions={defaultOfferAcceptorOptions}
                            loadOptions={(st) => loadOptions(st)}
                            getOptionLabel={(o) => `${o.email}`}
                            getOptionValue={(o) => o.id}
                            placeholder={i18n.t.common.select + "..."}
                            isDisabled={isDisabled || isLoading}
                            styles={reactSelectStyles(isInvalid)}
                            onChange={(value) => {
                              field.onChange(value);
                              submit();
                            }}
                          />
                        );
                      }}
                    </FormField>
                  )}
                />
              )}
            </>
          );
        }}
      </Form>
    );
  },
);

export default GeneralDataForm;
